// API Configuration for different environments
// This file centralizes all API endpoint configurations
// Synced with mobile app structure

interface ApiConfig {
  // Main Services
  baseUrl: string;              // Products Service (port 3004 local / 3000 AWS)
  authServiceUrl?: string;     // Auth Service (port 3001) - optional, defaults to baseUrl
  shopServiceUrl: string;       // Shop Service (port 3003)
  orderServiceUrl: string;     // Order Service (port 3005)
  paymentServiceUrl: string;   // Payment Service (port 3006 / 4555)
  aiAssistantUrl: string;      // AI Assistant Service (port 3009)
  notificationServiceUrl?: string; // Notification Service (port 3007)
  serviceBookingServiceUrl?: string; // Service Booking Service (port 3011)
  
  // WebSocket
  wsUrl: string;
  
  // Environment Info
  environment: string;
  debug: boolean;
  corsOrigin: string;
}

// LOCAL CONFIGURATION - For local development with auth service on port 3001
const LOCAL_CONFIG: ApiConfig = {
  baseUrl: 'http://localhost:3004/api/v1',           // Products Service (port 3004)
  authServiceUrl: 'http://localhost:3001/api/v1',   // Auth Service (port 3001)
  shopServiceUrl: 'http://localhost:3000/api',       // API Gateway (port 3000) - matches shop dashboard endpoint
  orderServiceUrl: 'http://localhost:3005/api/v1',   // Orders Service (port 3005)
  paymentServiceUrl: 'http://localhost:3006',        // Payments Service (port 3006)
  aiAssistantUrl: 'http://localhost:3009/api/v1',    // AI Assistant Service (port 3009)
  notificationServiceUrl: 'http://localhost:3007/api/v1', // Notification Service (port 3007)
  serviceBookingServiceUrl: 'http://localhost:3011/api/v1', // Service Booking Service (port 3011)
  wsUrl: 'ws://localhost:4555',
  environment: 'local-development',
  debug: true,
  corsOrigin: 'http://localhost:4000'
};

// AWS PRODUCTION CONFIGURATION - For production deployment
const AWS_CONFIG: ApiConfig = {
  baseUrl: 'http://43.205.243.9:3000/api',
  authServiceUrl: 'http://43.205.243.9:3001/api/v1',  // Auth Service
  shopServiceUrl: 'http://localhost:3003/api/v1',     // Shops (TODO: Update with AWS URL)
  orderServiceUrl: 'http://localhost:3005/api/v1',   // Orders (TODO: Update with AWS URL)
  paymentServiceUrl: 'http://3.110.219.37:3006/api', // Payments
  aiAssistantUrl: 'http://43.205.243.9:3007/api',     // AI Assistant
  notificationServiceUrl: 'http://43.205.243.9:3007/api/v1', // Notification Service
  serviceBookingServiceUrl: 'http://43.205.243.9:3011/api/v1', // Service Booking Service (TODO: Update with actual AWS URL)
  wsUrl: 'ws://43.205.243.9:3000',
  environment: 'aws-production',
  debug: false,
  corsOrigin: typeof window !== 'undefined' ? window.location.origin : ''
};

// Environment detection
const isDevelopment = process.env.NODE_ENV === 'development';
const isLocalhost = typeof window !== 'undefined' && (
  window.location.hostname === 'localhost' || 
  window.location.hostname === '127.0.0.1' ||
  window.location.hostname === '0.0.0.0'
);
const isPort4000 = typeof window !== 'undefined' && (
  window.location.port === '4000' || window.location.port === ''
);

// Check for explicit environment override
const forceLocal = process.env.REACT_APP_FORCE_LOCAL === 'true';
const forceAWS = process.env.REACT_APP_FORCE_AWS === 'true';

// Helper function to check if URL contains AWS IP or non-localhost
const isLocalhostUrl = (url: string): boolean => {
  if (!url) return true;
  const lowerUrl = url.toLowerCase();
  return lowerUrl.includes('localhost') || 
         lowerUrl.includes('127.0.0.1') || 
         lowerUrl.includes('0.0.0.0') ||
         lowerUrl.startsWith('/');
};

// Helper function to force localhost if AWS IP detected
// Special handling for authServiceUrl to always use port 3001
const forceLocalhostUrl = (url: string | undefined, defaultUrl: string, isAuthService: boolean = false): string => {
  if (!url) return defaultUrl;
  const trimmed = url.trim();
  
  // Special case: Always force port 3001 for auth service
  if (isAuthService && !trimmed.includes('3001')) {
    console.warn(`⚠️ Auth service URL should use port 3001, but got: ${trimmed}`);
    console.log(`✅ Forcing auth service to: http://localhost:3001/api/v1`);
    return 'http://localhost:3001/api/v1';
  }
  
  // If it contains AWS IP or non-localhost, replace with localhost equivalent
  if (!isLocalhostUrl(trimmed)) {
    console.warn(`⚠️ Detected non-localhost URL: ${trimmed}, forcing localhost`);
    // Extract port and path from the URL
    const match = trimmed.match(/:\d+(\/.*)?$/);
    if (match) {
      const portAndPath = match[0];
      return `http://localhost${portAndPath}`;
    }
    return defaultUrl;
  }
  
  // If it's port 3000 and should be auth service, force to 3001
  if (isAuthService && trimmed.includes('3000')) {
    console.warn(`⚠️ Auth service using port 3000, forcing to 3001`);
    return trimmed.replace(':3000', ':3001').replace('/api', '/api/v1');
  }
  
  return trimmed;
};

// Configuration selection logic
// DEFAULT TO LOCALHOST - Only use AWS if explicitly forced
const getApiConfig = (): ApiConfig => {
  // 1. Check for explicit force AWS flag (only way to use AWS)
  if (forceAWS) {
    console.log('☁️ Using AWS API configuration (FORCED)');
    return AWS_CONFIG;
  }

  // 2. Check for explicit environment variables (but validate and force localhost)
  const envApiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const envAuthUrl = process.env.REACT_APP_AUTH_SERVICE_URL;
  const envShopUrl = process.env.REACT_APP_SHOP_SERVICE_URL;
  const envOrderUrl = process.env.REACT_APP_ORDER_SERVICE_URL;
  const envPaymentUrl = process.env.REACT_APP_PAYMENT_SERVICE_URL;
  const envAiUrl = process.env.REACT_APP_AI_ASSISTANT_URL;
  const envServiceBookingUrl = process.env.REACT_APP_SERVICE_BOOKING_SERVICE_URL;
  const envWsUrl = process.env.REACT_APP_WS_URL;

  // If environment variables are set, validate and force localhost if needed
  if (envApiBaseUrl || envAuthUrl || envShopUrl || envOrderUrl || envPaymentUrl || envAiUrl || envServiceBookingUrl || envWsUrl) {
    console.log('🔧 Using CUSTOM API configuration from environment variables');
    // Force localhost for any AWS IPs detected
    const config = {
      baseUrl: forceLocalhostUrl(envApiBaseUrl, LOCAL_CONFIG.baseUrl),
      authServiceUrl: forceLocalhostUrl(envAuthUrl, LOCAL_CONFIG.authServiceUrl || 'http://localhost:3001/api/v1', true),
      shopServiceUrl: forceLocalhostUrl(envShopUrl, LOCAL_CONFIG.shopServiceUrl),
      orderServiceUrl: forceLocalhostUrl(envOrderUrl, LOCAL_CONFIG.orderServiceUrl),
      paymentServiceUrl: forceLocalhostUrl(envPaymentUrl, LOCAL_CONFIG.paymentServiceUrl),
      aiAssistantUrl: forceLocalhostUrl(envAiUrl, LOCAL_CONFIG.aiAssistantUrl),
      notificationServiceUrl: LOCAL_CONFIG.notificationServiceUrl,
      serviceBookingServiceUrl: forceLocalhostUrl(envServiceBookingUrl, LOCAL_CONFIG.serviceBookingServiceUrl || 'http://localhost:3011/api/v1'),
      wsUrl: forceLocalhostUrl(envWsUrl, LOCAL_CONFIG.wsUrl),
      environment: 'custom-local',
      debug: true,
      corsOrigin: typeof window !== 'undefined' ? window.location.origin : ''
    };
    console.log('✅ Forced all URLs to localhost:', config);
    return config;
  }

  // 3. Check for explicit force local flag
  if (forceLocal) {
    console.log('🏠 Using LOCAL API configuration (FORCED)');
    return LOCAL_CONFIG;
  }

  // 4. DEFAULT TO LOCALHOST - Always use localhost unless explicitly forced to AWS
  console.log('🏠 Using LOCAL API configuration (DEFAULT)');
  console.log('📍 Auth Service URL:', LOCAL_CONFIG.authServiceUrl);
  console.log('📍 Base URL:', LOCAL_CONFIG.baseUrl);
  console.log('💡 To use AWS, set REACT_APP_FORCE_AWS=true');
  return LOCAL_CONFIG;
};

export let apiConfig = getApiConfig();

// Runtime hostname detection - Override URLs for staging/production
// Similar to shop-dashboard approach - use relative URLs to avoid CORS
// Nginx will proxy /api/ requests to the actual API Gateway
if (typeof window !== 'undefined') {
  const hostname = window.location.hostname;
  const origin = window.location.origin;
  
  // Staging environment - ALWAYS use relative URLs (nginx proxies to stg-api.quickko.in)
  if (hostname.includes('stg-customer.quickko.in') || 
      hostname.includes('staging') || 
      hostname.includes('stg-')) {
    const originalConfig = { ...apiConfig };
    
    // Override all URLs to relative paths - nginx will proxy, no CORS
    apiConfig = {
      ...apiConfig,
      baseUrl: '/api',
      authServiceUrl: '/api',
      shopServiceUrl: '/api',
      orderServiceUrl: '/api',
      paymentServiceUrl: '/api',
      aiAssistantUrl: '/api',
      notificationServiceUrl: '/api',
      serviceBookingServiceUrl: '/api',
      wsUrl: window.location.protocol === 'https:' ? `wss://${hostname}/ws` : `ws://${hostname}/ws`,
      environment: 'staging',
      debug: true,
      corsOrigin: origin
    };
    
    // Always log this for debugging
    console.log('🔍 [STAGING] Overriding API URLs to relative paths:', {
      from: {
        baseUrl: originalConfig.baseUrl,
        wsUrl: originalConfig.wsUrl
      },
      to: {
        baseUrl: apiConfig.baseUrl,
        wsUrl: apiConfig.wsUrl
      },
      hostname: hostname,
      origin: origin
    });
  }
  // For local development (localhost), keep the full URLs from config
  else if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.')) {
    console.log('🔍 [LOCAL] Using full URLs from config:', {
      baseUrl: apiConfig.baseUrl,
      wsUrl: apiConfig.wsUrl,
      hostname: hostname
    });
    // Keep original apiConfig for local
  }
  // For any other hostname (unknown), also use relative URLs (safe default)
  else if (!isLocalhost) {
    const originalConfig = { ...apiConfig };
    apiConfig = {
      ...apiConfig,
      baseUrl: '/api',
      authServiceUrl: '/api',
      shopServiceUrl: '/api',
      orderServiceUrl: '/api',
      paymentServiceUrl: '/api',
      aiAssistantUrl: '/api',
      notificationServiceUrl: '/api',
      serviceBookingServiceUrl: '/api',
      wsUrl: window.location.protocol === 'https:' ? `wss://${hostname}/ws` : `ws://${hostname}/ws`,
      environment: 'unknown-host',
      debug: true,
      corsOrigin: origin
    };
    console.log('🔍 [UNKNOWN HOST] Using relative URLs as safe default:', {
      from: {
        baseUrl: originalConfig.baseUrl,
        wsUrl: originalConfig.wsUrl
      },
      to: {
        baseUrl: apiConfig.baseUrl,
        wsUrl: apiConfig.wsUrl
      },
      hostname: hostname
    });
  }
}

// Runtime validation - Force localhost if AWS IPs detected (only for local development)
const validateAndFixConfig = (config: ApiConfig): ApiConfig => {
  const awsIps = ['13.127.184.102', '43.205.243.9', '3.110.219.37'];
  let needsFix = false;
  
  const checkAndFix = (url: string | undefined, defaultUrl: string): string => {
    if (!url) return defaultUrl;
    const hasAwsIp = awsIps.some(ip => url.includes(ip));
    if (hasAwsIp) {
      console.error(`❌ BLOCKED AWS IP detected in URL: ${url}`);
      console.log(`✅ Replacing with localhost: ${defaultUrl}`);
      needsFix = true;
      return defaultUrl;
    }
    return url;
  };

  const fixedConfig: ApiConfig = {
    baseUrl: checkAndFix(config.baseUrl, LOCAL_CONFIG.baseUrl),
    authServiceUrl: checkAndFix(config.authServiceUrl, LOCAL_CONFIG.authServiceUrl || LOCAL_CONFIG.baseUrl),
    shopServiceUrl: checkAndFix(config.shopServiceUrl, LOCAL_CONFIG.shopServiceUrl),
    orderServiceUrl: checkAndFix(config.orderServiceUrl, LOCAL_CONFIG.orderServiceUrl),
    paymentServiceUrl: checkAndFix(config.paymentServiceUrl, LOCAL_CONFIG.paymentServiceUrl),
    aiAssistantUrl: checkAndFix(config.aiAssistantUrl, LOCAL_CONFIG.aiAssistantUrl),
    notificationServiceUrl: config.notificationServiceUrl || LOCAL_CONFIG.notificationServiceUrl,
    wsUrl: checkAndFix(config.wsUrl, LOCAL_CONFIG.wsUrl),
    environment: needsFix ? 'local-development-forced' : config.environment,
    debug: true,
    corsOrigin: config.corsOrigin || LOCAL_CONFIG.corsOrigin
  };

  if (needsFix) {
    console.warn('🔒 SECURITY: AWS IPs detected and blocked. Forcing localhost configuration.');
    apiConfig = fixedConfig;
    return fixedConfig;
  }

  return config;
};

// Apply validation
apiConfig = validateAndFixConfig(apiConfig);

// Detailed logging for debugging
console.log('🔧 API Configuration Details:', {
  selectedConfig: apiConfig.environment,
  baseUrl: apiConfig.baseUrl,
  authServiceUrl: apiConfig.authServiceUrl,
  shopServiceUrl: apiConfig.shopServiceUrl,
  orderServiceUrl: apiConfig.orderServiceUrl,
  paymentServiceUrl: apiConfig.paymentServiceUrl,
  aiAssistantUrl: apiConfig.aiAssistantUrl,
  notificationServiceUrl: apiConfig.notificationServiceUrl,
  serviceBookingServiceUrl: apiConfig.serviceBookingServiceUrl,
  wsUrl: apiConfig.wsUrl,
  debug: apiConfig.debug,
  detection: {
    isDevelopment,
    isLocalhost,
    isPort4000,
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'unknown',
    port: typeof window !== 'undefined' ? window.location.port : 'unknown',
    nodeEnv: process.env.NODE_ENV,
    forceLocal,
    forceAWS
  }
});

// Additional debug logging
if (apiConfig.debug) {
  console.log('🔍 Environment Variables Check:');
  console.log('REACT_APP_FORCE_LOCAL:', process.env.REACT_APP_FORCE_LOCAL);
  console.log('REACT_APP_FORCE_AWS:', process.env.REACT_APP_FORCE_AWS);
  console.log('REACT_APP_API_BASE_URL:', process.env.REACT_APP_API_BASE_URL);
  console.log('REACT_APP_AUTH_SERVICE_URL:', process.env.REACT_APP_AUTH_SERVICE_URL);
  console.log('Final baseUrl being used:', apiConfig.baseUrl);
  console.log('Final authServiceUrl being used:', apiConfig.authServiceUrl);
  
  // Warn if AWS IPs are still present (shouldn't happen after validation, except for staging/production)
  const awsIps = ['13.127.184.102', '43.205.243.9', '3.110.219.37'];
  const allUrls: string[] = [
    apiConfig.baseUrl,
    apiConfig.authServiceUrl,
    apiConfig.shopServiceUrl,
    apiConfig.orderServiceUrl,
    apiConfig.paymentServiceUrl,
    apiConfig.aiAssistantUrl,
    apiConfig.serviceBookingServiceUrl
  ].filter((url): url is string => Boolean(url));
  
  const hasAwsIp = allUrls.some(url => awsIps.some(ip => url.includes(ip)));
  const isStagingOrProduction = typeof window !== 'undefined' && (
    window.location.hostname.includes('stg-') || 
    window.location.hostname.includes('quickko.in')
  );
  
  if (hasAwsIp && !isStagingOrProduction) {
    console.error('🚨 CRITICAL: AWS IPs detected in final config! This should not happen.');
    console.error('Please restart the dev server and clear browser cache.');
  } else if (isStagingOrProduction) {
    console.log('✅ Using relative URLs for staging/production - nginx will proxy');
  } else {
    console.log('✅ All URLs validated - using localhost only');
  }
}

export default apiConfig;
